import { HomeOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { useFormikContext } from 'formik';
import { usePartyContext } from 'views/customers/customer/PartyProvider';
import CardWithIcon from 'components/new-ui/base/card/CardWithIcon';
import Button from 'components/new-ui/base/button/Button';
import { useProfilePicture } from 'hooks/useProfilePicture';
import {
    deleteCustomerProfilePicture,
    getCustomerProfilePictureDownloadPreSign,
    getCustomerProfilePictureUploadPreSign,
    uploadCustomerProfilePicture,
} from 'store/customers/client';
import PartyDetailsForm from './PartyDetailsForm';
import PartyDetailsView from './PartyDetailsView';
import { FormValues } from '../../types';

type Props = {
    isEditing: boolean;
    onConfirm: () => void;
    onEditClick: () => void;
    onCancel: () => void;
};

const PartyDetails = ({ isEditing, onConfirm, onEditClick, onCancel }: Props) => {
    const { canUpdate, viewOnly, customerId } = usePartyContext();
    const { isSubmitting } = useFormikContext<FormValues>();

    const { profilePicture, handleProfilePictureDelete, handleProfilePictureUpload } = useProfilePicture({
        getPicture: () => getCustomerProfilePictureDownloadPreSign(customerId),
        getPreSign: (file) =>
            getCustomerProfilePictureUploadPreSign(customerId!, {
                customerId: customerId!,
                pictureName: file.name,
                pictureSize: file.size,
            }),
        deletePicture: () => deleteCustomerProfilePicture(customerId!),
        uploadPicture: (preSignUrl) => uploadCustomerProfilePicture(customerId!, preSignUrl),
    });

    const buttonText = isSubmitting ? 'Saving...' : isEditing ? 'Save' : 'Edit';
    const submitHandler = isEditing ? onConfirm : onEditClick;
    const showEditButton = canUpdate && !viewOnly;

    return (
        <CardWithIcon
            title="Party Details"
            gap="large"
            bordered={false}
            iconProps={{
                icon: <HomeOutlined />,
                status: 'warning',
            }}
            headerActions={
                showEditButton && (
                    <Space size={8}>
                        {isEditing && (
                            <Button type="outlined" onClick={onCancel} disabled={isSubmitting}>
                                Cancel
                            </Button>
                        )}
                        <Button type="outlined" onClick={submitHandler}>
                            {buttonText}
                        </Button>
                    </Space>
                )
            }
            children={
                <div style={{ marginTop: 16 }}>
                    {isEditing ? (
                        <PartyDetailsForm
                            profilePicture={profilePicture}
                            handleProfilePictureUpload={handleProfilePictureUpload}
                            handleProfilePictureDelete={handleProfilePictureDelete}
                        />
                    ) : (
                        <PartyDetailsView profilePicture={profilePicture} />
                    )}
                </div>
            }
        />
    );
};

export default PartyDetails;
